import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import { 
  Factory, 
  Lock,
  Upload,
  PlusCircle,
  FileSpreadsheet,
  DollarSign,
  Square,
  TrendingUp,
  Search,
  Filter,
  X,
  MoreHorizontal,
  Edit,
  Copy,
  Trash2,
  Download,
  RefreshCw,
  Image,
  FolderOpen,
  Loader2,
  CheckSquare,
  Save,
  Merge,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import StatsCard from "@/components/common/StatsCard";
import { ImportCostosModal } from "@/components/factory/ImportCostosModal";
import { FactoryBulkMergeDialog } from "@/components/factory/FactoryBulkMergeDialog";
import { FactoryUndoMergeBanner } from "@/components/factory/FactoryUndoMergeBanner";
import { FactoryDuplicatesAlert } from "@/components/factory/FactoryDuplicatesAlert";
import { GlassNormalizationDialog } from "@/components/factory/GlassNormalizationDialog";
import ImageThumbnail from "@/components/common/ImageThumbnail";
import { formatNumber } from "@/utils/formatters";

const API = process.env.REACT_APP_BACKEND_URL;

// Custom hook for debounced value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

/**
 * FactoryView - Factory costs management view
 * Contains password gate, stats, upload section, and factory types table
 */
const FactoryView = ({
  // Auth state
  factoryAuthenticated,
  factoryPassword,
  setFactoryPassword,
  handleFactoryAuth,
  
  // Factory data
  factoryTypes,
  factoryStats,
  factoryStatsByFactory,
  availableFactories,
  materials,
  
  // Upload state
  isFactoryUploading,
  onFactoryFileUpload,
  onManualAddOpen,
  
  // Filter state
  factoryFilterFactory,
  setFactoryFilterFactory,
  factoryFilterMaterial,
  setFactoryFilterMaterial,
  factoryFilterColor,
  setFactoryFilterColor,
  factoryFilterGlass,
  setFactoryFilterGlass,
  factoryFilterExtras,
  setFactoryFilterExtras,
  factoryFilterProject,
  setFactoryFilterProject,
  factorySearchQuery,
  setFactorySearchQuery,
  
  // Actions
  openFactoryEditModal,
  openFactoryDuplicateModal,
  openFactoryDeleteModal,
  openImagePreview,
  refreshFactoryData,
  
  // Lazy loading props (NEW)
  totalItems = 0,
  hasMoreData = false,
  isLoadingMore = false,
  loadMoreFactoryTypes,
  reloadFactoryTypes,
  
  // Export props
  projectName = "EXPORT"
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingFull, setIsExportingFull] = useState(false);
  const [isRegeneratingHashes, setIsRegeneratingHashes] = useState(false);
  const [isImportCostosOpen, setIsImportCostosOpen] = useState(false);

  // ========== ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURN ==========
  
  // Debounce search query for server-side filtering
  const debouncedSearchQuery = useDebounce(factorySearchQuery, 500);
  
  // Ref for IntersectionObserver (lazy loading)
  const loadMoreRef = useRef(null);
  
  // Trigger server-side reload when filters change (only when authenticated)
  const prevFiltersRef = useRef({ 
    search: debouncedSearchQuery,
    material: factoryFilterMaterial,
    factory: factoryFilterFactory,
    color: factoryFilterColor,
    glass: factoryFilterGlass,
    extras: factoryFilterExtras,
    project: factoryFilterProject
  });
  
  useEffect(() => {
    if (!factoryAuthenticated || !reloadFactoryTypes) return;
    
    const currentFilters = {
      search: debouncedSearchQuery,
      material: factoryFilterMaterial,
      factory: factoryFilterFactory,
      color: factoryFilterColor,
      glass: factoryFilterGlass,
      extras: factoryFilterExtras,
      project: factoryFilterProject
    };
    
    // Only reload if filters actually changed
    const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(prevFiltersRef.current);
    
    if (filtersChanged) {
      prevFiltersRef.current = currentFilters;
      reloadFactoryTypes();
    }
  }, [factoryAuthenticated, debouncedSearchQuery, factoryFilterMaterial, factoryFilterFactory, factoryFilterColor, factoryFilterGlass, factoryFilterExtras, factoryFilterProject, reloadFactoryTypes]);
  
  // Refs for intersection observer values (to avoid stale closures)
  const hasMoreDataRef = useRef(hasMoreData);
  const isLoadingMoreRef = useRef(isLoadingMore);
  
  useEffect(() => {
    hasMoreDataRef.current = hasMoreData;
  }, [hasMoreData]);
  
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);
  
  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const targetElement = loadMoreRef.current;
    if (!targetElement || !loadMoreFactoryTypes) {
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMoreDataRef.current && !isLoadingMoreRef.current) {
          loadMoreFactoryTypes();
        }
      },
      { 
        root: null, // Use viewport as root
        threshold: 0.1,
        rootMargin: '200px' // Preload 200px before reaching the bottom
      }
    );
    
    observer.observe(targetElement);
    
    return () => {
      observer.disconnect();
    };
  }, [loadMoreFactoryTypes]);
  
  // Server-side filtered data - no frontend filtering needed
  const filteredFactoryTypes = factoryTypes;
  
  // Show loading indicator while search is being debounced
  const isSearching = factorySearchQuery !== debouncedSearchQuery;

  // Global filter options (server-side distinct) merged with values from the loaded page
  const [filterOptions, setFilterOptions] = useState({ colors: [], glass_specs: [], extra_specs: [], series: [] });
  useEffect(() => {
    if (!factoryAuthenticated || !factoryPassword) return;
    api.get(`${API}/api/factory/filters/options`, { headers: { "X-Factory-Password": factoryPassword } })
      .then((res) => setFilterOptions(res.data))
      .catch((error) => console.error("Filter options error:", error));
  }, [factoryAuthenticated, factoryPassword, factoryTypes.length]);

  const mergeUnique = (globalList, pageValues) =>
    [...new Set([...(globalList || []), ...pageValues.filter(Boolean)])].sort((a, b) => a.localeCompare(b));

  // Memoized unique values from factory types (for dropdown options)
  const availableColors = useMemo(() => 
    mergeUnique(filterOptions.colors, factoryTypes.map(t => t.color)),
    [factoryTypes, filterOptions.colors]
  );
  const availableGlassSpecs = useMemo(() =>
    mergeUnique(filterOptions.glass_specs, factoryTypes.map(t => t.glass_spec)),
    [factoryTypes, filterOptions.glass_specs]
  );
  const availableExtraSpecs = useMemo(() =>
    mergeUnique(filterOptions.extra_specs, factoryTypes.map(t => t.extra_specs)),
    [factoryTypes, filterOptions.extra_specs]
  );
  const availableProjects = useMemo(() =>
    [...new Set(factoryTypes.map(t => t.project_name).filter(Boolean))].sort(),
    [factoryTypes]
  );
  
  // ========== END OF HOOKS ==========

  // ========== BULK SELECTION STATE ==========
  const [selectedFactoryIds, setSelectedFactoryIds] = useState(new Set());
  const [showBulkEditPanel, setShowBulkEditPanel] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    material: "",
    color: "",
    series: "",
    glass_spec: "",
    opening_design: "",
    factory_name: ""
  });
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [showBulkMergeDialog, setShowBulkMergeDialog] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState(null);
  const [lastMerge, setLastMerge] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [isLoadingDuplicates, setIsLoadingDuplicates] = useState(false);
  const [dupOptions, setDupOptions] = useState({ byColor: false, byFactory: false });
  const [normalizeField, setNormalizeField] = useState(null); // 'glass_spec' | 'color' | null
  const selectedFactoryTypes = useMemo(
    () => factoryTypes.filter((t) => selectedFactoryIds.has(t.id)),
    [factoryTypes, selectedFactoryIds]
  );
  const mergeDialogTypes = mergeCandidates || selectedFactoryTypes;

  const fetchDuplicates = useCallback(async () => {
    if (!factoryAuthenticated || !factoryPassword) return;
    setIsLoadingDuplicates(true);
    try {
      const res = await api.get(`${API}/api/factory/duplicates`, {
        params: { by_color: dupOptions.byColor, by_factory: dupOptions.byFactory },
        headers: { "X-Factory-Password": factoryPassword }
      });
      setDuplicates(res.data);
    } catch (error) {
      console.error("Duplicates fetch error:", error);
    } finally {
      setIsLoadingDuplicates(false);
    }
  }, [factoryAuthenticated, factoryPassword, dupOptions]);

  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates]);

  // Toggle selection
  const toggleFactorySelection = (id) => {
    setSelectedFactoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all visible
  const selectAllVisible = () => {
    const visibleIds = new Set(filteredFactoryTypes.map(t => t.id));
    setSelectedFactoryIds(visibleIds);
    setShowBulkEditPanel(true);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedFactoryIds(new Set());
    setShowBulkEditPanel(false);
    setBulkEditForm({
      material: "",
      color: "",
      series: "",
      glass_spec: "",
      opening_design: "",
      factory_name: ""
    });
  };

  const refreshAfterMerge = () => {
    clearSelection();
    if (reloadFactoryTypes) reloadFactoryTypes();
    else refreshFactoryData?.();
    fetchDuplicates();
  };

  // Apply bulk edit
  const handleBulkEdit = async () => {
    if (selectedFactoryIds.size === 0) {
      toast.error("Selecciona al menos un elemento");
      return;
    }

    // Build update object with only non-empty fields
    const updates = {};
    if (bulkEditForm.material) updates.material = bulkEditForm.material;
    if (bulkEditForm.color) updates.color = bulkEditForm.color;
    if (bulkEditForm.series) updates.series = bulkEditForm.series;
    if (bulkEditForm.glass_spec) updates.glass_spec = bulkEditForm.glass_spec;
    if (bulkEditForm.opening_design) updates.opening_design = bulkEditForm.opening_design;
    if (bulkEditForm.factory_name) updates.factory_name = bulkEditForm.factory_name;

    if (Object.keys(updates).length === 0) {
      toast.error("Completa al menos un campo para actualizar");
      return;
    }

    setIsBulkSaving(true);
    try {
      const response = await api.put(`${API}/api/factory/bulk-update`, {
        ids: [...selectedFactoryIds],
        updates: updates,
        password: factoryPassword
      });

      toast.success(`${response.data.updated} elementos actualizados correctamente`);
      clearSelection();
      
      // Refresh factory data
      if (refreshFactoryData) {
        refreshFactoryData();
      }
    } catch (error) {
      console.error("Bulk update error:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar elementos");
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Handle regenerate image hashes
  const handleRegenerateHashes = async () => {
    setIsRegeneratingHashes(true);
    const API = process.env.REACT_APP_BACKEND_URL + "/api";
    
    try {
      const response = await api.post(
        `${API}/factory/regenerate-image-hashes`,
        null,
        { headers: { 'X-Factory-Password': factoryPassword } }
      );
      
      const { updated, errors, message } = response.data;
      
      if (updated > 0) {
        toast.success(message);
      } else {
        toast.info("No hay registros que necesiten actualización de image_hash");
      }
      
      if (errors && errors.length > 0) {
        // console.warn("Errores al regenerar hashes:", errors);
      }
    } catch (error) {
      // console.error("Error regenerando hashes:", error);
      toast.error(error.response?.data?.detail || "Error al regenerar image hashes");
    } finally {
      setIsRegeneratingHashes(false);
    }
  };

  // Handle Excel export (COTI EN EXCEL)
  const handleExportExcel = async () => {
    if (!factoryTypes || factoryTypes.length === 0) {
      toast.error("No hay elementos para exportar");
      return;
    }

    setIsExporting(true);
    const API = process.env.REACT_APP_BACKEND_URL + "/api";

    try {
      // Prepare items for export
      const items = factoryTypes.map(type => ({
        item_code: type.item_code || type.name,
        width_m: type.entries?.[0]?.width_m || 0,
        height_m: type.entries?.[0]?.height_m || 0,
        quantity: type.entries_count || 1,
        area_m2: type.total_area / (type.entries_count || 1),
        cost_per_m2: type.average_cost_per_m2 || 0
      }));

      // Export to Excel
      const response = await api.post(
        `${API}/factory/export-excel`,
        {
          items: items,
          uti_percent: 0,
          project_name: projectName
        },
        {
          responseType: 'blob'
        }
      );

      // Download file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `COTI_${projectName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel exportado correctamente");

    } catch (error) {
      // console.error("Error exporting:", error);
      toast.error(error.response?.data?.detail || "Error al exportar Excel");
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Full Excel Export (comprehensive factory data)
  const handleExportFullExcel = async (includeEntries = false) => {
    setIsExportingFull(true);
    const API = process.env.REACT_APP_BACKEND_URL + "/api";

    try {
      const response = await api.post(
        `${API}/factory/export-full-excel`,
        {
          password: factoryPassword,
          factory_filter: factoryFilterFactory || null,
          material_filter: factoryFilterMaterial || null,
          include_entries: includeEntries
        },
        {
          responseType: 'blob'
        }
      );

      // Download file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Costos_Fabrica_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const totalItems = response.headers['x-total-items'] || factoryTypes.length;
      toast.success(`Excel exportado: ${totalItems} items`);

    } catch (error) {
      // console.error("Error exporting full Excel:", error);
      toast.error(error.response?.data?.detail || "Error al exportar Excel completo");
    } finally {
      setIsExportingFull(false);
    }
  };

  // If not authenticated, show password gate
  if (!factoryAuthenticated) {
    return (
      <div className="space-y-8" data-testid="factory-view-auth">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Costos de Fábrica
            </CardTitle>
            <CardDescription>
              Ingresa la contraseña para acceder a los costos de fábrica
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                data-testid="factory-password-input"
                type="password"
                placeholder="Contraseña..."
                value={factoryPassword}
                onChange={(e) => setFactoryPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFactoryAuth()}
              />
            </div>
            <Button 
              onClick={handleFactoryAuth} 
              className="w-full"
              data-testid="factory-auth-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Acceder
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="factory-view">
      {/* Stats Row */}
      <div className="bento-grid">
        <div className="bento-item-third">
          <StatsCard
            icon={Factory}
            label="Tipos de Fábrica"
            value={factoryStats?.total_types || 0}
            subValue={`${factoryStats?.total_entries || 0} entradas totales`}
          />
        </div>
        <div className="bento-item-third">
          <StatsCard
            icon={DollarSign}
            label="Promedio Fábrica"
            value={`$${formatNumber(factoryStats?.overall_average_cost_per_m2 || 0)}`}
            subValue="USD por m²"
          />
        </div>
        <div className="bento-item-third">
          <StatsCard
            icon={Square}
            label="Área Total"
            value={`${formatNumber(factoryStats?.total_area_indexed || 0)} m²`}
            subValue="de costos procesados"
          />
        </div>
      </div>

      {/* Upload Factory File */}
      <Card className="metric-card">
        <CardHeader className="border-b border-slate-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Cargar Archivo de Fábrica
              </CardTitle>
              <CardDescription className="text-slate-500">
                Sube archivos Excel (Conch, MOBILIARS) o PDF (ACACIAS, ARTLINE)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={onManualAddOpen}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="factory-manual-add-btn"
              >
                <PlusCircle className="w-4 h-4" />
                Agregar Manual
              </Button>
              <Button
                onClick={() => setIsImportCostosOpen(true)}
                variant="outline"
                className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
                data-testid="factory-import-costos-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Importar Costos
              </Button>
              <Button
                onClick={handleRegenerateHashes}
                variant="outline"
                className="flex items-center gap-2"
                disabled={isRegeneratingHashes || factoryTypes.length === 0}
                title="Regenerar image_hash para mejorar el auto-match por imagen"
                data-testid="factory-regenerate-hashes-btn"
              >
                <Image className="w-4 h-4" />
                {isRegeneratingHashes ? "Procesando..." : "Regen. Hashes"}
              </Button>
              <Button
                onClick={() => setNormalizeField("glass_spec")}
                variant="outline"
                className="flex items-center gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                title="Unificar valores equivalentes de vidrio (10MM, 10mm, 10mm Tempered…) en un solo nombre"
                data-testid="factory-normalize-glass-btn"
              >
                <Sparkles className="w-4 h-4" />
                Normalizar Vidrios
              </Button>
              <Button
                onClick={() => setNormalizeField("color")}
                variant="outline"
                className="flex items-center gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                title="Unificar colores equivalentes (Black / BLACK / Negro, White / Blanco…) en un solo nombre"
                data-testid="factory-normalize-color-btn"
              >
                <Sparkles className="w-4 h-4" />
                Normalizar Colores
              </Button>
              
              {/* Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    disabled={(isExporting || isExportingFull) || factoryTypes.length === 0}
                    data-testid="factory-export-dropdown-btn"
                  >
                    <Download className="w-4 h-4" />
                    {(isExporting || isExportingFull) ? "Exportando..." : "Exportar Excel"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem 
                    onClick={handleExportExcel}
                    disabled={isExporting}
                    data-testid="factory-export-coti-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    COTI (Básico)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => handleExportFullExcel(false)}
                    disabled={isExportingFull}
                    data-testid="factory-export-full-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Índice Completo
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleExportFullExcel(true)}
                    disabled={isExportingFull}
                    data-testid="factory-export-full-entries-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Completo + Detalle
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div
            data-testid="factory-upload-dropzone"
            className="upload-zone flex flex-col items-center justify-center cursor-pointer"
            onClick={() => document.getElementById('factory-file-input').click()}
          >
            <input
              id="factory-file-input"
              type="file"
              accept=".xlsx,.xls,.pdf"
              className="hidden"
              onChange={(e) => e.target.files[0] && onFactoryFileUpload(e.target.files[0])}
              data-testid="factory-file-input"
            />
            
            {isFactoryUploading ? (
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Analizando archivo...</p>
              </div>
            ) : (
              <>
                <FileSpreadsheet className="w-12 h-12 text-slate-400 mb-4" />
                <p className="text-slate-700 font-medium mb-2">
                  Arrastra tu archivo aquí
                </p>
                <p className="text-slate-500 text-sm mb-4">
                  o haz clic para seleccionar
                </p>
                <div className="flex gap-2 flex-wrap justify-center">
                  <Badge variant="secondary" className="text-xs">Excel Conch</Badge>
                  <Badge variant="secondary" className="text-xs">Excel MOBILIARS</Badge>
                  <Badge variant="outline" className="text-xs">PDF ACACIAS</Badge>
                  <Badge variant="outline" className="text-xs">PDF ARTLINE</Badge>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Factory Stats by Factory Card */}
      {factoryStatsByFactory.length > 0 && (
        <Card className="metric-card">
          <CardHeader className="border-b border-slate-100 p-4">
            <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Comparativa por Fábrica
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {factoryStatsByFactory.map((stat) => (
                <div key={stat.factory_name} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-900">{stat.factory_name}</h4>
                    <Badge variant="outline">{stat.total_types} tipos</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Promedio:</span>
                      <span className="ml-1 font-medium text-green-600">${stat.average_cost_per_m2}/m²</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Área:</span>
                      <span className="ml-1 font-medium">{stat.total_area}m²</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Mín:</span>
                      <span className="ml-1 text-blue-600">${stat.min_cost_per_m2}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Máx:</span>
                      <span className="ml-1 text-red-600">${stat.max_cost_per_m2}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Merge helpers */}
      <FactoryUndoMergeBanner
        lastMerge={lastMerge}
        factoryPassword={factoryPassword}
        onUndone={() => { setLastMerge(null); refreshAfterMerge(); }}
        onExpired={() => setLastMerge(null)}
      />
      <FactoryDuplicatesAlert
        duplicates={duplicates}
        isLoading={isLoadingDuplicates}
        options={dupOptions}
        onOptionsChange={setDupOptions}
        onRefresh={fetchDuplicates}
        onMergeGroup={(types) => { setMergeCandidates(types); setShowBulkMergeDialog(true); }}
        factoryPassword={factoryPassword}
        onBatchMerged={(result) => { setLastMerge(result); refreshAfterMerge(); }}
      />

      {/* Factory Types Table */}
      <Card className="metric-card">
        <CardHeader className="border-b border-slate-100 p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
                  <Factory className="w-5 h-5" />
                  Costos de Fábrica Indexados
                </CardTitle>
                <CardDescription className="text-slate-500">
                  {filteredFactoryTypes.length} de {totalItems || factoryTypes.length} tipos
                  {hasMoreData && <span className="text-blue-500 ml-1">(scroll para más)</span>}
                </CardDescription>
              </div>
              {/* Search Input */}
              <div className="relative">
                {isSearching ? (
                  <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin" />
                ) : (
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                )}
                <Input
                  placeholder="Buscar por nombre, diseño..."
                  value={factorySearchQuery}
                  onChange={(e) => setFactorySearchQuery(e.target.value)}
                  className="pl-9 h-8 w-[250px] text-sm"
                  data-testid="factory-search-input"
                />
                {factorySearchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setFactorySearchQuery("")}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Filters Row */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500 font-medium">Filtros:</span>
              </div>
              
              {/* Material Filter */}
              <Select value={factoryFilterMaterial} onValueChange={setFactoryFilterMaterial}>
                <SelectTrigger className="w-[120px] h-8 text-sm">
                  <SelectValue placeholder="Material" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo Material</SelectItem>
                  {materials.map((mat) => (
                    <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Color Filter */}
              <Select value={factoryFilterColor} onValueChange={setFactoryFilterColor}>
                <SelectTrigger className="w-[120px] h-8 text-sm">
                  <SelectValue placeholder="Color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo Color</SelectItem>
                  {availableColors.map((color) => (
                    <SelectItem key={color} value={color}>{color}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Glass Filter (NEW) */}
              {availableGlassSpecs.length > 0 && (
                <Select value={factoryFilterGlass || "all"} onValueChange={setFactoryFilterGlass}>
                  <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="filter-glass">
                    <SelectValue placeholder="Vidrio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todo Vidrio</SelectItem>
                    {availableGlassSpecs.map((glass) => (
                      <SelectItem key={glass} value={glass}>{glass}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Extra Specs / Varios Filter (NEW) */}
              {availableExtraSpecs.length > 0 && (
                <Select value={factoryFilterExtras || "all"} onValueChange={setFactoryFilterExtras}>
                  <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="filter-extras">
                    <SelectValue placeholder="Varios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {availableExtraSpecs.map((extra) => (
                      <SelectItem key={extra} value={extra}>{extra}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Factory Filter */}
              {availableFactories.length > 0 && (
                <Select value={factoryFilterFactory} onValueChange={setFactoryFilterFactory}>
                  <SelectTrigger className="w-[150px] h-8 text-sm">
                    <SelectValue placeholder="Fábrica" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toda Fábrica</SelectItem>
                    {availableFactories.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Project Filter */}
              {availableProjects.length > 0 && (
                <Select value={factoryFilterProject || "all"} onValueChange={setFactoryFilterProject}>
                  <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="filter-project">
                    <SelectValue placeholder="Proyecto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todo Proyecto</SelectItem>
                    {availableProjects.map((proj) => (
                      <SelectItem key={proj} value={proj}>{proj}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Clear Filters */}
              {(factoryFilterMaterial !== "all" || factoryFilterColor !== "all" || factoryFilterFactory !== "all" || 
                (factoryFilterGlass && factoryFilterGlass !== "all") || (factoryFilterExtras && factoryFilterExtras !== "all") || 
                (factoryFilterProject && factoryFilterProject !== "all") || factorySearchQuery) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setFactoryFilterMaterial("all");
                    setFactoryFilterColor("all");
                    setFactoryFilterFactory("all");
                    if (setFactoryFilterGlass) setFactoryFilterGlass("all");
                    if (setFactoryFilterExtras) setFactoryFilterExtras("all");
                    if (setFactoryFilterProject) setFactoryFilterProject("all");
                    setFactorySearchQuery("");
                  }}
                  className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Bulk Edit Panel */}
          {selectedFactoryIds.size > 0 && (
            <div className="p-4 bg-blue-50 border-b border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    {selectedFactoryIds.size} elemento{selectedFactoryIds.size === 1 ? '' : 's'} seleccionado{selectedFactoryIds.size === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearSelection}
                    className="text-blue-600 border-blue-300"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Deseleccionar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkMergeDialog(true)}
                    disabled={selectedFactoryIds.size < 2}
                    title={selectedFactoryIds.size < 2 ? "Selecciona al menos 2 tipos para fusionar" : "Fusionar los tipos seleccionados en uno"}
                    className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    data-testid="factory-bulk-merge-btn"
                  >
                    <Merge className="w-4 h-4 mr-2" />
                    Fusionar Selección
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowBulkEditPanel(!showBulkEditPanel)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {showBulkEditPanel ? 'Ocultar Panel' : 'Editar Selección'}
                  </Button>
                </div>
              </div>
              
              {/* Bulk Edit Form */}
              {showBulkEditPanel && (
                <div className="p-4 bg-white rounded-lg border border-blue-200 space-y-4">
                  <p className="text-sm text-slate-600 mb-3">
                    Completa los campos que deseas actualizar. Los campos vacíos no se modificarán.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Material</Label>
                      <Select 
                        value={bulkEditForm.material} 
                        onValueChange={(v) => setBulkEditForm(prev => ({ ...prev, material: v }))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Sin cambio" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Aluminio">Aluminio</SelectItem>
                          <SelectItem value="uPVC">uPVC</SelectItem>
                          <SelectItem value="UPVC">UPVC</SelectItem>
                          <SelectItem value="Vidrio">Vidrio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Color</Label>
                      <Input
                        value={bulkEditForm.color}
                        onChange={(e) => setBulkEditForm(prev => ({ ...prev, color: e.target.value }))}
                        placeholder="Sin cambio"
                        className="h-9"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Serie</Label>
                      <Input
                        value={bulkEditForm.series}
                        onChange={(e) => setBulkEditForm(prev => ({ ...prev, series: e.target.value }))}
                        placeholder="Sin cambio"
                        className="h-9"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Vidrio</Label>
                      <Input
                        value={bulkEditForm.glass_spec}
                        onChange={(e) => setBulkEditForm(prev => ({ ...prev, glass_spec: e.target.value }))}
                        placeholder="Sin cambio"
                        className="h-9"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Diseño Apertura</Label>
                      <Input
                        value={bulkEditForm.opening_design}
                        onChange={(e) => setBulkEditForm(prev => ({ ...prev, opening_design: e.target.value }))}
                        placeholder="Sin cambio"
                        className="h-9"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Fábrica</Label>
                      <Input
                        value={bulkEditForm.factory_name}
                        onChange={(e) => setBulkEditForm(prev => ({ ...prev, factory_name: e.target.value }))}
                        placeholder="Sin cambio"
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkEditForm({
                        material: "",
                        color: "",
                        series: "",
                        glass_spec: "",
                        opening_design: "",
                        factory_name: ""
                      })}
                    >
                      Limpiar Campos
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleBulkEdit}
                      disabled={isBulkSaving}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isBulkSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Aplicando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Aplicar a {selectedFactoryIds.size} elemento{selectedFactoryIds.size > 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {factoryTypes.length === 0 ? (
            <div className="empty-state">
              <Factory className="empty-state-icon" />
              <p className="text-slate-600 font-medium">Sin datos de fábrica</p>
              <p className="text-slate-400 text-sm mt-1">
                Carga un archivo Excel o PDF para comenzar
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="industrial-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedFactoryIds.size === filteredFactoryTypes.length && filteredFactoryTypes.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            selectAllVisible();
                          } else {
                            clearSelection();
                          }
                        }}
                        data-testid="select-all-factory"
                      />
                    </TableHead>
                    <TableHead className="w-[70px]">Imagen</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Diseño</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>Serie</TableHead>
                    <TableHead className="text-right">Muestras</TableHead>
                    <TableHead className="text-right">Promedio/m² (USD)</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFactoryTypes.map((type) => {
                    const isSelected = selectedFactoryIds.has(type.id);
                    return (
                    <TableRow key={type.id} className={`table-row-hover ${isSelected ? 'bg-blue-50' : ''}`}>
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleFactorySelection(type.id)}
                          data-testid={`select-factory-${type.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <ImageThumbnail
                          src={type.image_data}
                          alt={type.name}
                          className="w-12 h-12 rounded border border-slate-200"
                          onClick={() => type.image_data && openImagePreview(type.image_data, type.name)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{type.item_code || type.name}</TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {type.opening_design || type.name}
                        {type.opening_design && type.name && type.opening_design !== type.name && (
                          <div className="text-xs text-slate-500 mt-0.5">{type.name}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={type.material === "Aluminio" ? "default" : "secondary"}>
                          {type.material}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{type.color || "—"}</TableCell>
                      <TableCell className="text-slate-500">{type.series || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{type.entries_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-bold text-emerald-600">
                          ${formatNumber(type.average_cost_per_m2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`factory-row-actions-${type.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openFactoryEditModal(type)} data-testid="factory-edit-menu-item">
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openFactoryDuplicateModal(type)} data-testid="factory-duplicate-menu-item">
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              data-testid="factory-delete-menu-item"
                              onClick={() => openFactoryDeleteModal(type)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Load more trigger for infinite scroll */}
              <div 
                ref={loadMoreRef} 
                className="h-20 flex flex-col items-center justify-center gap-2"
                data-testid="factory-load-more-trigger"
              >
                {isLoadingMore && (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Cargando más tipos...</span>
                  </div>
                )}
                {!isLoadingMore && hasMoreData && (
                  <div className="flex flex-col items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={loadMoreFactoryTypes}
                      data-testid="factory-load-more-btn"
                    >
                      Cargar más tipos ({filteredFactoryTypes.length} de {totalItems})
                    </Button>
                    <span className="text-xs text-slate-400">o haz scroll para cargar automáticamente</span>
                  </div>
                )}
                {!hasMoreData && filteredFactoryTypes.length > 0 && (
                  <div className="text-sm text-slate-400">
                    {totalItems > 0 ? `Mostrando todos los ${totalItems} tipos` : 'Fin de los resultados'}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Costos Modal */}
      <GlassNormalizationDialog
        open={normalizeField !== null}
        onOpenChange={(v) => { if (!v) setNormalizeField(null); }}
        field={normalizeField || "glass_spec"}
        factoryPassword={factoryPassword}
        onApplied={() => { if (reloadFactoryTypes) reloadFactoryTypes(); else refreshFactoryData?.(); }}
      />

      <FactoryBulkMergeDialog
        open={showBulkMergeDialog}
        onOpenChange={(v) => { setShowBulkMergeDialog(v); if (!v) setMergeCandidates(null); }}
        selectedTypes={mergeDialogTypes}
        factoryPassword={factoryPassword}
        onMerged={(result) => {
          setLastMerge(result);
          refreshAfterMerge();
        }}
      />

      <ImportCostosModal
        isOpen={isImportCostosOpen}
        onClose={() => setIsImportCostosOpen(false)}
        factoryPassword={factoryPassword}
        onImportComplete={(result) => {
          setIsImportCostosOpen(false);
          // Show success message with stats
          if (result?.stats) {
            const { factory_imported, factory_with_images, quote_types_new } = result.stats;
            toast.success(
              `Importación completada: ${factory_imported || 0} items${factory_with_images ? ` (${factory_with_images} con imagen)` : ''}${quote_types_new ? `, ${quote_types_new} tipos nuevos` : ''}`,
              { duration: 5000 }
            );
          }
          // Refresh factory data without page reload
          if (refreshFactoryData) {
            refreshFactoryData();
          }
        }}
      />
    </div>
  );
};

export default FactoryView;
